import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";

const TOKEN_KEY = "casas_tracking_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("checking"); // checking | authed | guest

    const setAuthHeader = useCallback((token) => {
        if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        else delete api.defaults.headers.common["Authorization"];
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthHeader(null);
        setUser(null);
        setStatus("guest");
    }, [setAuthHeader]);

    // Initial bootstrap: read token, verify with /auth/me
    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setStatus("guest");
            return;
        }
        setAuthHeader(token);
        api.get("/auth/me")
            .then((r) => {
                setUser(r.data);
                setStatus("authed");
            })
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setAuthHeader(null);
                setStatus("guest");
            });
    }, [setAuthHeader]);

    // Global axios interceptor: on 401, logout
    useEffect(() => {
        const id = api.interceptors.response.use(
            (r) => r,
            (err) => {
                if (err?.response?.status === 401) {
                    localStorage.removeItem(TOKEN_KEY);
                    setAuthHeader(null);
                    setUser(null);
                    setStatus("guest");
                }
                return Promise.reject(err);
            },
        );
        return () => api.interceptors.response.eject(id);
    }, [setAuthHeader]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem(TOKEN_KEY, data.token);
        setAuthHeader(data.token);
        setUser(data.user);
        setStatus("authed");
        return data.user;
    }, [setAuthHeader]);

    return (
        <AuthContext.Provider value={{
            user, status, login, logout,
            isAdmin: user?.role === "admin",
            isViewer: user?.role === "lector",
            canWrite: user?.role !== "lector",
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
